import axios from "axios";
import { useQuery, useQueries, useQueryClient } from "react-query";

const headers = { "Content-Type": "application/json" };

// Create separate unauthenticated instance for localhost (CORS compat) -BG
const axioslocal = axios.create({
  withCredentials: false,
  baseURL: process.env.REACT_APP_CONNECTOR_ROUTE,
});

axios.defaults.baseURL = process.env.REACT_APP_API_URL;
axios.defaults.withCredentials = true;

export * from "./sample.js";

export const useSequence = (library) => {
  return useQuery(["sequence", library], async () => {
    const { data } = await axios.get("/food/new-sample/sequence-list", {
      params: { library: library },
    });
    return data;
  });
};

// Modify the useRecentSamples API to query data for a specific date range
export const useRecentSamples = (dateRange, selectedLocation, deviceType) => {
  return useQuery(
    ["byLoc", "recentSamples", dateRange, selectedLocation, deviceType],
    async () => {
      const { data } = await axios.get(`/food/recent-samples/recentSamples`, {
        params: {
          paststart: dateRange.start,
          pastend: dateRange.end,
          selectedLocation: selectedLocation,
          deviceType: deviceType,
        },
      });

      return data;
    },
    {
      // staleTime: 0,
      // Add a queryKey function to include the date range in the query key
      queryKey: ["byLoc", "recentSamples", dateRange],
    }
  );
};
export const useAVRExperiments = (dateRange, selectedLocation) => {
  return useQuery(
    ["byLoc", "recentSamples", dateRange, selectedLocation],
    async () => {
      const { data } = await axios.get(`/food/recent-samples/AVRExperiments`, {
        params: {
          paststart: dateRange.start,
          pastend: dateRange.end,
          selectedLocation: selectedLocation,
        },
      });

      return data;
    },
    {
      // staleTime: 0,
      // Add a queryKey function to include the date range in the query key
      queryKey: ["byLoc", "AVRExperiments", dateRange],
    }
  );
};

export const useExperiment = (id) => {
  return useQuery(["experiment", id], async () => {
    const { data } = await axios.get("/food/recent-samples/get-experiment", {
      params: { id: id },
    });
    return data;
  });
};

export const useScanData = (ids) => {
  return useQueries(
    ids.map((id) => {
      return {
        queryKey: ["scanData", id],
        queryFn: async () => {
          const { data } = await axios.get("/food/recent-samples/get-scans", {
            params: { id: id },
          });
          return data;
        },
        enabled: false,
      };
    })
  );
};
export const useGetChipExportData = (ids) => {
  return useQueries(
    ids.map((id) => {
      return {
        queryKey: ["chipExportData", id],
        queryFn: async () => {
          const { data } = await axios.get(
            "/food/recent-samples/get-chip-export-data",
            {
              params: { id: id },
            }
          );
          return data;
        },
        enabled: false,
      };
    })
  );
};

export const useGetChipData = (id, isActive) => {
  // if (Array.isArray(id)) {
  //   return useQueries(
  //     id.map((id) => {
  //       return {
  //         queryKey: ["getChipData", id],
  //         queryFn: async () => {
  //           const { data } = await axios.get(
  //             `/food/view-edit-sample/get-chip-data`,
  //             {
  //               params: { id: id },
  //             }
  //           );

  //           return data;
  //         },
  //       };
  //     }),
  //     {
  //       enabled: isActive,
  //     }
  //   );
  // }
  return useQuery(
    ["getChipData", id],
    async () => {
      const { data } = await axios.get(`/food/view-edit-sample/get-chip-data`, {
        params: { id: id },
      });

      return data;
    },
    {
      enabled: isActive,
    }
  );
};

export const useParams = () => {
  return useQuery("params", async () => {
    const { data } = await axios.get("/food/new-sample/params");
    return data;
  });
};

export const useSample = (id) => {
  return useQuery(["sample", id], async () => {
    const { data } = await axios.get("/food/view-edit-sample/get-sample", {
      params: { id: id },
    });

    return data;
  });
};

export const useSampleStats = (ldate, hdate, location) => {
  return useQuery(
    ["byLoc", "sampleStats", ldate, hdate, location],
    async () => {
      const { data } = await axios.post("/food/body-stats/samples", {
        ldate,
        hdate,
        location,
      });
      return data;
    }
  );
};

export const useScores = (
  ldate,
  hdate,
  prevldate,
  prevhdate,
  selectedLocation
) => {
  return useQuery(
    ["byLoc", "scores", ldate, hdate, prevldate, prevhdate, selectedLocation],
    async () => {
      const { data } = await axios.post("/food/body-stats/scores", {
        ldate,
        hdate,
        prevldate,
        prevhdate,
        selectedLocation,
      });
      return data;
    }
  );
};

export const useContaminationPlotData = (ldate, selectedLocation) => {
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return useQuery(
    ["byLoc", "contaminationPlotData", ldate, userTimezone, selectedLocation],
    async () => {
      const { data } = await axios.post(
        "/food/body-stats/contaminationPlotData",
        {
          ldate,
          userTimezone,
          selectedLocation,
        }
      );
      return data;
    }
  );
};

export const useDefaultDeviceManufacturer = () => {
  return useQuery([], async () => {
    const { data } = await axios.get("/food/new-sample/defaultDevice");
    console.log(data);
    return data;
  });
};

// Retrieves a list of connected device ids from the connector app
export const useConnectorDevices = (successFunc, errorLogger) => {
  return useQuery(
    "connectorDevices",
    async () => {
      let response = {};

      await axioslocal
        .get(`${process.env.REACT_APP_CONNECTOR_ROUTE}/scan-devices`)
        .then((result) => {
          response = result;
        })
        .catch((err) => {
          console.error(err);
          response.status = 400;
          if (!err.response) {
            response.message = "Connector application could not be found.";
          } else {
            response.message = err.response?.data?.detail
              ? "Error from connector app: " + err.response?.data?.detail
              : "No devices could be returned by connector application.";
          }
        });

      // Update the data array to be a list of the serials of the devices returned
      if (response && response.data && response.data.devices) {
        // the deviceId = "serial" of the device
        response.devices = response.data.devices.map((device) => device.serial);
      } else {
        response.status = 400;
        response.devices = [];
      }
      return response;
    },
    {
      onSuccess: successFunc,
      onError: errorLogger,
    }
  );
};

// Using a list of ids, retrieves information about those devices from
// the database (using the back-end) and returns a list of device objs
export const useServerDevices = (data, successFunc, errorLogger) => {
  return useQuery(
    ["serverDevices", data],
    async () => {
      if (!data || !data.devices || data.devices.length === 0) {
        return [];
      }
      let ids = data.devices;
      let devices = [];

      await Promise.all(
        ids.map(async (id) => {
          await axios
            .get(`/devices/${id}`)
            .then((result) => {
              devices.push(result.data);
            })
            .catch((err) => {
              console.error(err);
              console.log(
                `Could not retrieve device of id (serial) ${id} from the server.`
              );
            });
        })
      );
      return devices;
    },
    {
      onSuccess: successFunc,
      onError: errorLogger,
    }
  );
};

// Begins the calibration process by attempting to use the calibrate route
// through the connector
export const useConnectorCalibrate = (id, successFunc, errorLogger) => {
  return useQuery(
    ["connectorCalibrate", id],
    async () => {
      // use /calibrate-device
      let response = {};
      await axioslocal
        .post(
          `${process.env.REACT_APP_CONNECTOR_ROUTE}/calibrate-device`,
          {
            deviceSerial: id,
            scanType: "trace",
          },
          {
            headers: headers,
          }
        )
        .then((result) => {
          response = result;
        })
        .catch((err) => {
          console.error(err);
          response.status = 400;
          if (!err.response) {
            response.message = "Connector application could not be found.";
          } else {
            response.message = err.response?.data?.detail
              ? "Error from connector app: " + err.response?.data?.detail
              : "Error calibrating device through connector application.";
          }
        });

      return response;
    },
    {
      // Don't run query by default (only on refetch)
      enabled: false,
      onSuccess: successFunc,
      onError: errorLogger,
    }
  );
};

// Save the current date (and time) of the current calibration to the backend
// (server)
export const useServerCalibrate = (id, date, successFunc, errorLogger) => {
  return useQuery(
    ["serverCalibrate", id],
    async () => {
      let response = {};
      await axios
        .patch(`/devices/${id}`, {
          lastCalibration: date,
        })
        .then((result) => {
          response = result;
        })
        .catch((err) => {
          console.error(err);
          response.status = 400;
          response.message = "Error saving calibration date to server.";
        });

      return response;
    },
    {
      // Don't run query by default (only on refetch)
      enabled: false,
      onSuccess: successFunc,
      onError: errorLogger,
    }
  );
};

// get measurement data (background scan) from connected device
export const useGetMeasurementBackground = (
  deviceSerial,
  scanType,
  errorLogger
) => {
  // const date = new Date();
  // const sampleData = {
  // "date": `${date}`,
  // "deviceSerial": "WP-00829",
  // "deviceModel": "WP-785-R-SR-LMM-25-OEM-V3",
  // "scanType": "Trace",
  // "exposureTime": 3,
  // "laserPower": 20,
  // "wavenumber": [-323.59, -320.16, -316.73, -313.3, -309.88, -306.46, -303.04, -299.62, -296.2, -292.79, -289.37, -285.96, -282.55, -279.15, -275.74, -272.34, -268.94, -265.53, -262.14, -258.74, -255.35, -251.95, -248.56, -245.17, -241.79, -238.4, -235.02, -231.63, -228.25, -224.88, -221.5, -218.13, -214.75, -211.38, -208.01, -204.65, -201.28, -197.92, -194.56, -191.2, -187.84, -184.48, -181.13, -177.78, -174.43, -171.08, -167.73, -164.39, -161.04, -157.7, -154.36, -151.03, -147.69, -144.36, -141.03, -137.7, -134.37, -131.04, -127.72, -124.4, -121.07, -117.76, -114.44, -111.12, -107.81, -104.5, -101.19, -97.88, -94.58, -91.27, -87.97, -84.67, -81.37, -78.08, -74.78, -71.49, -68.2, -64.91, -61.63, -58.34, -55.06, -51.78, -48.5, -45.22, -41.94, -38.67, -35.4, -32.13, -28.86, -25.59, -22.33, -19.07, -15.81, -12.55, -9.29, -6.04, -2.78, 0.47, 3.72, 6.96, 10.21, 13.45, 16.69, 19.93, 23.17, 26.41, 29.64, 32.87, 36.1, 39.33, 42.56, 45.78, 49.0, 52.23, 55.44, 58.66, 61.88, 65.09, 68.3, 71.51, 74.72, 77.92, 81.13, 84.33, 87.53, 90.73, 93.92, 97.12, 100.31, 103.5, 106.69, 109.87, 113.06, 116.24, 119.42, 122.6, 125.78, 128.95, 132.12, 135.29, 138.46, 141.63, 144.8, 147.96, 151.12, 154.28, 157.44, 160.6, 163.75, 166.9, 170.05, 173.2, 176.35, 179.49, 182.63, 185.77, 188.91, 192.05, 195.18, 198.32, 201.45, 204.58, 207.7, 210.83, 213.95, 217.07, 220.19, 223.31, 226.42, 229.54, 232.65, 235.76, 238.87, 241.97, 245.08, 248.18, 251.28, 254.38, 257.47, 260.57, 263.66, 266.75, 269.84, 272.93, 276.01, 279.09, 282.17, 285.25, 288.33, 291.41, 294.48, 297.55, 300.62, 303.69, 306.75, 309.81, 312.88, 315.94, 318.99, 322.05, 325.1, 328.15, 331.2, 334.25, 337.3, 340.34, 343.38, 346.42, 349.46, 352.5, 355.53, 358.57, 361.6, 364.63, 367.65, 370.68, 373.7, 376.72, 379.74, 382.76, 385.77, 388.78, 391.79, 394.8, 397.81, 400.82, 403.82, 406.82, 409.82, 412.82, 415.81, 418.81, 421.8, 424.79, 427.78, 430.76, 433.75, 436.73, 439.71, 442.69, 445.66, 448.64, 451.61, 454.58, 457.55, 460.51, 463.48, 466.44, 469.4, 472.36, 475.32, 478.27, 481.22, 484.17, 487.12, 490.07, 493.01, 495.96, 498.9, 501.84, 504.77, 507.71, 510.64, 513.57, 516.5, 519.43, 522.35, 525.28, 528.2, 531.12, 534.04, 536.95, 539.87, 542.78, 545.69, 548.59, 551.5, 554.4, 557.31, 560.21, 563.1, 566.0, 568.89, 571.79, 574.68, 577.56, 580.45, 583.34, 586.22, 589.1, 591.98, 594.85, 597.73, 600.6, 603.47, 606.34, 609.21, 612.07, 614.93, 617.79, 620.65, 623.51, 626.36, 629.22, 632.07, 634.92, 637.76, 640.61, 643.45, 646.29, 649.13, 651.97, 654.8, 657.64, 660.47, 663.3, 666.13, 668.95, 671.77, 674.6, 677.42, 680.23, 683.05, 685.86, 688.67, 691.48, 694.29, 697.1, 699.9, 702.7, 705.5, 708.3, 711.1, 713.89, 716.68, 719.47, 722.26, 725.05, 727.83, 730.61, 733.39, 736.17, 738.95, 741.72, 744.5, 747.27, 750.03, 752.8, 755.57, 758.33, 761.09, 763.85, 766.6, 769.36, 772.11, 774.86, 777.61, 780.36, 783.1, 785.85, 788.59, 791.33, 794.06, 796.8, 799.53, 802.26, 804.99, 807.72, 810.44, 813.17, 815.89, 818.61, 821.32, 824.04, 826.75, 829.46, 832.17, 834.88, 837.59, 840.29, 842.99, 845.69, 848.39, 851.08, 853.78, 856.47, 859.16, 861.85, 864.53, 867.22, 869.9, 872.58, 875.26, 877.93, 880.61, 883.28, 885.95, 888.62, 891.28, 893.95, 896.61, 899.27, 901.93, 904.58, 907.24, 909.89, 912.54, 915.19, 917.83, 920.48, 923.12, 925.76, 928.4, 931.04, 933.67, 936.3, 938.93, 941.56, 944.19, 946.81, 949.44, 952.06, 954.68, 957.29, 959.91, 962.52, 965.13, 967.74, 970.35, 972.95, 975.56, 978.16, 980.76, 983.35, 985.95, 988.54, 991.13, 993.72, 996.31, 998.89, 1001.48, 1004.06, 1006.64, 1009.22, 1011.79, 1014.37, 1016.94, 1019.51, 1022.07, 1024.64, 1027.2, 1029.76, 1032.32, 1034.88, 1037.44, 1039.99, 1042.54, 1045.09, 1047.64, 1050.19, 1052.73, 1055.27, 1057.81, 1060.35, 1062.89, 1065.42, 1067.95, 1070.48, 1073.01, 1075.54, 1078.06, 1080.58, 1083.1, 1085.62, 1088.14, 1090.65, 1093.16, 1095.67, 1098.18, 1100.69, 1103.19, 1105.7, 1108.2, 1110.7, 1113.19, 1115.69, 1118.18, 1120.67, 1123.16, 1125.65, 1128.13, 1130.61, 1133.09, 1135.57, 1138.05, 1140.52, 1143.0, 1145.47, 1147.94, 1150.4, 1152.87, 1155.33, 1157.79, 1160.25, 1162.71, 1165.16, 1167.62, 1170.07, 1172.52, 1174.97, 1177.41, 1179.85, 1182.3, 1184.74, 1187.17, 1189.61, 1192.04, 1194.47, 1196.9, 1199.33, 1201.76, 1204.18, 1206.6, 1209.02, 1211.44, 1213.85, 1216.27, 1218.68, 1221.09, 1223.5, 1225.9, 1228.31, 1230.71, 1233.11, 1235.51, 1237.9, 1240.3, 1242.69, 1245.08, 1247.47, 1249.85, 1252.24, 1254.62, 1257.0, 1259.38, 1261.76, 1264.13, 1266.5, 1268.87, 1271.24, 1273.61, 1275.97, 1278.34, 1280.7, 1283.06, 1285.41, 1287.77, 1290.12, 1292.47, 1294.82, 1297.17, 1299.51, 1301.85, 1304.2, 1306.53, 1308.87, 1311.21, 1313.54, 1315.87, 1318.2, 1320.53, 1322.85, 1325.18, 1327.5, 1329.82, 1332.13, 1334.45, 1336.76, 1339.07, 1341.38, 1343.69, 1346.0, 1348.3, 1350.6, 1352.9, 1355.2, 1357.49, 1359.79, 1362.08, 1364.37, 1366.66, 1368.94, 1371.23, 1373.51, 1375.79, 1378.07, 1380.34, 1382.62, 1384.89, 1387.16, 1389.43, 1391.69, 1393.96, 1396.22, 1398.48, 1400.74, 1403.0, 1405.25, 1407.5, 1409.75, 1412.0, 1414.25, 1416.49, 1418.74, 1420.98, 1423.22, 1425.45, 1427.69, 1429.92, 1432.15, 1434.38, 1436.61, 1438.83, 1441.06, 1443.28, 1445.5, 1447.71, 1449.93, 1452.14, 1454.35, 1456.56, 1458.77, 1460.97, 1463.18, 1465.38, 1467.58, 1469.78, 1471.97, 1474.17, 1476.36, 1478.55, 1480.73, 1482.92, 1485.1, 1487.29, 1489.47, 1491.64, 1493.82, 1495.99, 1498.17, 1500.34, 1502.5, 1504.67, 1506.83, 1509.0, 1511.16, 1513.31, 1515.47, 1517.63, 1519.78, 1521.93, 1524.08, 1526.22, 1528.37, 1530.51, 1532.65, 1534.79, 1536.93, 1539.06, 1541.19, 1543.33, 1545.45, 1547.58, 1549.71, 1551.83, 1553.95, 1556.07, 1558.19, 1560.3, 1562.41, 1564.53, 1566.64, 1568.74, 1570.85, 1572.95, 1575.05, 1577.15, 1579.25, 1581.35, 1583.44, 1585.53, 1587.62, 1589.71, 1591.79, 1593.88, 1595.96, 1598.04, 1600.12, 1602.19, 1604.27, 1606.34, 1608.41, 1610.48, 1612.54, 1614.61, 1616.67, 1618.73, 1620.79, 1622.85, 1624.9, 1626.95, 1629.0, 1631.05, 1633.1, 1635.14, 1637.19, 1639.23, 1641.27, 1643.3, 1645.34, 1647.37, 1649.4, 1651.43, 1653.46, 1655.48, 1657.51, 1659.53, 1661.55, 1663.56, 1665.58, 1667.59, 1669.61, 1671.61, 1673.62, 1675.63, 1677.63, 1679.63, 1681.63, 1683.63, 1685.63, 1687.62, 1689.61, 1691.6, 1693.59, 1695.58, 1697.56, 1699.54, 1701.52, 1703.5, 1705.48, 1707.45, 1709.42, 1711.39, 1713.36, 1715.33, 1717.29, 1719.26, 1721.22, 1723.18, 1725.13, 1727.09, 1729.04, 1730.99, 1732.94, 1734.89, 1736.83, 1738.78, 1740.72, 1742.66, 1744.59, 1746.53, 1748.46, 1750.39, 1752.32, 1754.25, 1756.18, 1758.1, 1760.02, 1761.94, 1763.86, 1765.77, 1767.69, 1769.6, 1771.51, 1773.42, 1775.32, 1777.23, 1779.13, 1781.03, 1782.93, 1784.82, 1786.72, 1788.61, 1790.5, 1792.39, 1794.28, 1796.16, 1798.04, 1799.92, 1801.8, 1803.68, 1805.56, 1807.43, 1809.3, 1811.17, 1813.04, 1814.9, 1816.76, 1818.62, 1820.48, 1822.34, 1824.2, 1826.05, 1827.9, 1829.75, 1831.6, 1833.44, 1835.29, 1837.13, 1838.97, 1840.81, 1842.64, 1844.48, 1846.31, 1848.14, 1849.97, 1851.79, 1853.62, 1855.44, 1857.26, 1859.08, 1860.89, 1862.71, 1864.52, 1866.33, 1868.14, 1869.95, 1871.75, 1873.55, 1875.35, 1877.15, 1878.95, 1880.75, 1882.54, 1884.33, 1886.12, 1887.91, 1889.69, 1891.47, 1893.26, 1895.03, 1896.81, 1898.59, 1900.36, 1902.13, 1903.9, 1905.67, 1907.44, 1909.2, 1910.96, 1912.72, 1914.48, 1916.24, 1917.99, 1919.74, 1921.49, 1923.24, 1924.99, 1926.73, 1928.47, 1930.21, 1931.95, 1933.69, 1935.42, 1937.16, 1938.89, 1940.61, 1942.34, 1944.07, 1945.79, 1947.51, 1949.23, 1950.95, 1952.66, 1954.37, 1956.09, 1957.79, 1959.5, 1961.21, 1962.91, 1964.61, 1966.31, 1968.01, 1969.71, 1971.4, 1973.09, 1974.78, 1976.47, 1978.15, 1979.84, 1981.52, 1983.2, 1984.88, 1986.56, 1988.23, 1989.9, 1991.57, 1993.24, 1994.91, 1996.57, 1998.23, 1999.89, 2001.55, 2003.21, 2004.86, 2006.52, 2008.17, 2009.82, 2011.46, 2013.11, 2014.75, 2016.39, 2018.03, 2019.67, 2021.31, 2022.94, 2024.57, 2026.2, 2027.83, 2029.45, 2031.08, 2032.7, 2034.32, 2035.94, 2037.55, 2039.17, 2040.78, 2042.39, 2044.0, 2045.6, 2047.21, 2048.81, 2050.41, 2052.01, 2053.6, 2055.2, 2056.79, 2058.38, 2059.97, 2061.56, 2063.14, 2064.72, 2066.31, 2067.88, 2069.46, 2071.04, 2072.61, 2074.18, 2075.75, 2077.32, 2078.88, 2080.45, 2082.01, 2083.57, 2085.12, 2086.68, 2088.23, 2089.79, 2091.33, 2092.88, 2094.43, 2095.97, 2097.51, 2099.05, 2100.59, 2102.13, 2103.66, 2105.19, 2106.73, 2108.25, 2109.78, 2111.3, 2112.83, 2114.35, 2115.87, 2117.38, 2118.9, 2120.41, 2121.92, 2123.43, 2124.94, 2126.44, 2127.94, 2129.45, 2130.94, 2132.44, 2133.94, 2135.43, 2136.92, 2138.41, 2139.9, 2141.38, 2142.87, 2144.35, 2145.83, 2147.31, 2148.78, 2150.26, 2151.73, 2153.2, 2154.67, 2156.13, 2157.6, 2159.06, 2160.52, 2161.98, 2163.43, 2164.89, 2166.34, 2167.79, 2169.24, 2170.68, 2172.13, 2173.57],
  // "scan": [1283.0, 1215.0, 1245.0, 1268.0, 1317.0, 1306.0, 1328.0, 1283.0, 1287.0, 1290.0, 1188.0, 1252.0, 1268.0, 1260.0, 1332.0, 1351.0, 1344.0, 1325.0, 1366.0, 1465.0, 1363.0, 1378.0, 1503.0, 1328.0, 1347.0, 1393.0, 1412.0, 1382.0, 1431.0, 1465.0, 1420.0, 1355.0, 1347.0, 1408.0, 1427.0, 1404.0, 1427.0, 1439.0, 1378.0, 1366.0, 1423.0, 1382.0, 1344.0, 1355.0, 1336.0, 1389.0, 1401.0, 1579.0, 1469.0, 1378.0, 1332.0, 1370.0, 1378.0, 1366.0, 1351.0, 1378.0, 1374.0, 1427.0, 1454.0, 1370.0, 1408.0, 1389.0, 1393.0, 1416.0, 1363.0, 1401.0, 1454.0, 1393.0, 1374.0, 1351.0, 1385.0, 1412.0, 1351.0, 1435.0, 1427.0, 1473.0, 1416.0, 1423.0, 1431.0, 1397.0, 1340.0, 1416.0, 1442.0, 1366.0, 1420.0, 1469.0, 1442.0, 1435.0, 1458.0, 1488.0, 1363.0, 1446.0, 1484.0, 1431.0, 1340.0, 1328.0, 1404.0, 1435.0, 1325.0, 1366.0, 1374.0, 1412.0, 1404.0, 1385.0, 1382.0, 1412.0, 1374.0, 1458.0, 1469.0, 1446.0, 1401.0, 1404.0, 1382.0, 1355.0, 1484.0, 1454.0, 1404.0, 1416.0, 1511.0, 1526.0, 1420.0, 1469.0, 1495.0, 1454.0, 1442.0, 1503.0, 1469.0, 1492.0, 1541.0, 1488.0, 1435.0, 1473.0, 1522.0, 1533.0, 1465.0, 1461.0, 1514.0, 1480.0, 1450.0, 1416.0, 1416.0, 1465.0, 1476.0, 1461.0, 1480.0, 1499.0, 1492.0, 1484.0, 1530.0, 1469.0, 1522.0, 1495.0, 1484.0, 1458.0, 1427.0, 1461.0, 1537.0, 1609.0, 1514.0, 1583.0, 1526.0, 1484.0, 1488.0, 1454.0, 1507.0, 1446.0, 1503.0, 1571.0, 1571.0, 1628.0, 1727.0, 1959.0, 2635.0, 3682.0, 5649.0, 8789.0, 12841.0, 16542.0, 19162.0, 20609.0, 21080.0, 20962.0, 20487.0, 19888.0, 19181.0, 18619.0, 18202.0, 17663.0, 17393.0, 17142.0, 16956.0, 16808.0, 16558.0, 16444.0, 16546.0, 16406.0, 16353.0, 16429.0, 16436.0, 16611.0, 16550.0, 16524.0, 16520.0, 16615.0, 16615.0, 16721.0, 16668.0, 16820.0, 16968.0, 17021.0, 17139.0, 17241.0, 17173.0, 17363.0, 17325.0, 17359.0, 17283.0, 17408.0, 17427.0, 17328.0, 17336.0, 17408.0, 17499.0, 17552.0, 17697.0, 17864.0, 17974.0, 17936.0, 18103.0, 18194.0, 18221.0, 18346.0, 18433.0, 18547.0, 18756.0, 18916.0, 19018.0, 19189.0, 19470.0, 19614.0, 19645.0, 19827.0, 19815.0, 19952.0, 19975.0, 19880.0, 19944.0, 19963.0, 20093.0, 20077.0, 20036.0, 20096.0, 20286.0, 20351.0, 20366.0, 20430.0, 20468.0, 20552.0, 20730.0, 20734.0, 20966.0, 21209.0, 21577.0, 22139.0, 22929.0, 23438.0, 23221.0, 22712.0, 22181.0, 21983.0, 21926.0, 22025.0, 22094.0, 22181.0, 22295.0, 22496.0, 22747.0, 22876.0, 22948.0, 22990.0, 23009.0, 23088.0, 23130.0, 23111.0, 23081.0, 23020.0, 23009.0, 23077.0, 23157.0, 23085.0, 23115.0, 23066.0, 23157.0, 23100.0, 23088.0, 23369.0, 23407.0, 23453.0, 23362.0, 23468.0, 23495.0, 23438.0, 23601.0, 23658.0, 23722.0, 23757.0, 23874.0, 24064.0, 24155.0, 24262.0, 24364.0, 24497.0, 24516.0, 24725.0, 24801.0, 24960.0, 25025.0, 25226.0, 25385.0, 25420.0, 25408.0, 25370.0, 25499.0, 25560.0, 25545.0, 25515.0, 25602.0, 25583.0, 25515.0, 25488.0, 25575.0, 25564.0, 25674.0, 25583.0, 25727.0, 25811.0, 25864.0, 25906.0, 26114.0, 26149.0, 26236.0, 26403.0, 26479.0, 26608.0, 26760.0, 26760.0, 26840.0, 27109.0, 27216.0, 27102.0, 27238.0, 27246.0, 27284.0, 27405.0, 27610.0, 27793.0, 27804.0, 27853.0, 27876.0, 28036.0, 28176.0, 28184.0, 28282.0, 28241.0, 28301.0, 28351.0, 28332.0, 28374.0, 28286.0, 28351.0, 28252.0, 28290.0, 28294.0, 28313.0, 28248.0, 28381.0, 28552.0, 28552.0, 28601.0, 28655.0, 28810.0, 28768.0, 28879.0, 28970.0, 29061.0, 29087.0, 29080.0, 29122.0, 29334.0, 29308.0, 29220.0, 29338.0, 29357.0, 29327.0, 29448.0, 29547.0, 29437.0, 29452.0, 29395.0, 29604.0, 29554.0, 29592.0, 29475.0, 29490.0, 29528.0, 29524.0, 29577.0, 29725.0, 29756.0, 29854.0, 29926.0, 30040.0, 29953.0, 30052.0, 29995.0, 30044.0, 30018.0, 30021.0, 30078.0, 29953.0, 29999.0, 29953.0, 29995.0, 29987.0, 29964.0, 29999.0, 30162.0, 30219.0, 30219.0, 30261.0, 30310.0, 30287.0, 30477.0, 30488.0, 30507.0, 30458.0, 30530.0, 30659.0, 30773.0, 30640.0, 30686.0, 30705.0, 30857.0, 30769.0, 30766.0, 30724.0, 30879.0, 30898.0, 30990.0, 31115.0, 30917.0, 30974.0, 31069.0, 31073.0, 31088.0, 30933.0, 30929.0, 30811.0, 30891.0, 31005.0, 30959.0, 30974.0, 31160.0, 31134.0, 31244.0, 31233.0, 31396.0, 31445.0, 31468.0, 31403.0, 31476.0, 31491.0, 31597.0, 31548.0, 31320.0, 31438.0, 31529.0, 31552.0, 31525.0, 31571.0, 31434.0, 31624.0, 31741.0, 31669.0, 31817.0, 32011.0, 31958.0, 31965.0, 31943.0, 32053.0, 32026.0, 31931.0, 31825.0, 31692.0, 31650.0, 31605.0, 31601.0, 31472.0, 31419.0, 31479.0, 31434.0, 31388.0, 31434.0, 31563.0, 31517.0, 31593.0, 31552.0, 31476.0, 31476.0, 31586.0, 31552.0, 31498.0, 31514.0, 31491.0, 31502.0, 31521.0, 31476.0, 31426.0, 31590.0, 31662.0, 31760.0, 31795.0, 31943.0, 32038.0, 32038.0, 32068.0, 32068.0, 32182.0, 32231.0, 32182.0, 32300.0, 32246.0, 32265.0, 32231.0, 32239.0, 32186.0, 32091.0, 32079.0, 32094.0, 32167.0, 32197.0, 32281.0, 32448.0, 32410.0, 32368.0, 32505.0, 32459.0, 32387.0, 32417.0, 32429.0, 32402.0, 32478.0, 32273.0, 32284.0, 32197.0, 32155.0, 32106.0, 31996.0, 32019.0, 31992.0, 32091.0, 32254.0, 32117.0, 32178.0, 32182.0, 32224.0, 32300.0, 32235.0, 32254.0, 32113.0, 32159.0, 32151.0, 32110.0, 32193.0, 32292.0, 32136.0, 32398.0, 32113.0, 32113.0, 32049.0, 32022.0, 32212.0, 32182.0, 32368.0, 32467.0, 32698.0, 32778.0, 32694.0, 32668.0, 32649.0, 32797.0, 32926.0, 33002.0, 32679.0, 32664.0, 32573.0, 32455.0, 32512.0, 32303.0, 32174.0, 32174.0, 32182.0, 32284.0, 32174.0, 32117.0, 32167.0, 32212.0, 32167.0, 32076.0, 32110.0, 32060.0, 32072.0, 32026.0, 32072.0, 31973.0, 31943.0, 32091.0, 32094.0, 32277.0, 32364.0, 32637.0, 32543.0, 32611.0, 32668.0, 32748.0, 32831.0, 32934.0, 32903.0, 32975.0, 32968.0, 32930.0, 33066.0, 32877.0, 32877.0, 32679.0, 32493.0, 32379.0, 32201.0, 32224.0, 32068.0, 31795.0, 31867.0, 31776.0, 31738.0, 31874.0, 31825.0, 31863.0, 31624.0, 31764.0, 31806.0, 31844.0, 31772.0, 31855.0, 31814.0, 31776.0, 31760.0, 31616.0, 31457.0, 31612.0, 31578.0, 31582.0, 31339.0, 31407.0, 31350.0, 31331.0, 31381.0, 31438.0, 31472.0, 31548.0, 31563.0, 31472.0, 31487.0, 31662.0, 31582.0, 31544.0, 31392.0, 31491.0, 31438.0, 31335.0, 31350.0, 31430.0, 31339.0, 31312.0, 31176.0, 31240.0, 31217.0, 31271.0, 31468.0, 31286.0, 31320.0, 31381.0, 31419.0, 31438.0, 31422.0, 31536.0, 31776.0, 31696.0, 31741.0, 31768.0, 31924.0, 31829.0, 31738.0, 31741.0, 31669.0, 31582.0, 31491.0, 31506.0, 31620.0, 31597.0, 31571.0, 31479.0, 31464.0, 31821.0, 31316.0, 31240.0, 31255.0, 31297.0, 31259.0, 31297.0, 31309.0, 31316.0, 31122.0, 31191.0, 31233.0, 31233.0, 31195.0, 31202.0, 31191.0, 31050.0, 31104.0, 31100.0, 31028.0, 31001.0, 31062.0, 31104.0, 31107.0, 31236.0, 31240.0, 31255.0, 31274.0, 31305.0, 31377.0, 31453.0, 31415.0, 31510.0, 31468.0, 31491.0, 31593.0, 31529.0, 31445.0, 31358.0, 31236.0, 31373.0, 31233.0, 31176.0, 31248.0, 31225.0, 31176.0, 31058.0, 30963.0, 31122.0, 31016.0, 31024.0, 30978.0, 31024.0, 31024.0, 31005.0, 31001.0, 30971.0, 30804.0, 30750.0, 30857.0, 30724.0, 30804.0, 30879.0, 30819.0, 30724.0, 30663.0, 30709.0, 30693.0, 30785.0, 30830.0, 30853.0, 30868.0, 30857.0, 30990.0, 31043.0, 31009.0, 31145.0, 31198.0, 31316.0, 31290.0, 31297.0, 31324.0, 31377.0, 31715.0, 31244.0, 31278.0, 31426.0, 31464.0, 31324.0, 31396.0, 31278.0, 31301.0, 31217.0, 31301.0, 31343.0, 31282.0, 31400.0, 31365.0, 31244.0, 31324.0, 31407.0, 31388.0, 31411.0, 31293.0, 31347.0, 31339.0, 31206.0, 31134.0, 31145.0, 31195.0, 31085.0, 31115.0, 31062.0, 31043.0, 31012.0, 31062.0, 31031.0, 31138.0, 30997.0, 31115.0, 30944.0, 31066.0, 30879.0, 31077.0, 30955.0, 30948.0, 30891.0, 30940.0, 30823.0, 30940.0, 30819.0, 30838.0, 30804.0, 30830.0, 30777.0, 30921.0, 30883.0, 30906.0, 30769.0, 30724.0, 30716.0, 30815.0, 30940.0, 30906.0, 30959.0, 30891.0, 30906.0, 31066.0, 31012.0, 31035.0, 30955.0, 30982.0, 30948.0, 30883.0, 30857.0, 31001.0, 30978.0, 31039.0, 31012.0, 30952.0, 30986.0, 31164.0, 31244.0, 31267.0, 31373.0, 31233.0, 31271.0, 31221.0, 31286.0, 31248.0, 31255.0, 31168.0, 31104.0, 31130.0, 31100.0, 31210.0, 31179.0, 31157.0, 31157.0, 31149.0, 31160.0, 31096.0, 31096.0, 31202.0, 31153.0, 31100.0, 30978.0, 30986.0, 31077.0, 30955.0, 30948.0, 31035.0, 30868.0, 30879.0, 30853.0, 30845.0, 30743.0, 30731.0, 30701.0, 30785.0, 30712.0, 30709.0, 30636.0, 30610.0, 30530.0, 30515.0, 30515.0, 30431.0, 30435.0, 30443.0, 30450.0, 30409.0, 30504.0, 30420.0, 30386.0, 30420.0, 30462.0, 30412.0, 30356.0, 30302.0, 30371.0, 30344.0, 30325.0, 30409.0, 30280.0, 30325.0, 30348.0, 30185.0, 30211.0, 30257.0, 30173.0, 30120.0, 30192.0, 30173.0, 30132.0, 30109.0, 30063.0, 29907.0, 29858.0, 29820.0, 29763.0, 29752.0, 29604.0, 29554.0, 29494.0, 29456.0, 29353.0, 29285.0, 29228.0, 29034.0, 28939.0, 28943.0, 28780.0, 28666.0, 28480.0, 28541.0, 28294.0, 28309.0, 28104.0, 27956.0, 27815.0, 27641.0, 27576.0, 27493.0, 27424.0, 27212.0, 27242.0, 27026.0, 26870.0, 26733.0, 26627.0, 26441.0, 26399.0, 26297.0, 26152.0, 25921.0, 25784.0, 25594.0, 25682.0, 25465.0, 25477.0, 25203.0, 25051.0, 24907.0, 24744.0, 24736.0, 24634.0, 24558.0, 24349.0, 24307.0, 24144.0, 23996.0, 23924.0, 23730.0, 23544.0, 23415.0, 23305.0, 23214.0, 23035.0, 23058.0, 22936.0, 22857.0, 22674.0, 22496.0, 22363.0, 22192.0, 22048.0, 22048.0, 21771.0, 21566.0, 21372.0, 20848.0],
  // "scanDark": [1116.0, 1055.0, 1082.0, 1093.0, 1123.0, 1127.0, 1135.0, 1112.0, 1097.0, 1093.0, 1032.0, 1082.0, 1085.0, 1074.0, 1146.0, 1146.0, 1154.0, 1116.0, 1169.0, 1211.0, 1161.0, 1192.0, 1268.0, 1131.0, 1165.0, 1184.0, 1196.0, 1188.0, 1226.0, 1264.0, 1203.0, 1173.0, 1154.0, 1218.0, 1222.0, 1180.0, 1218.0, 1215.0, 1192.0, 1169.0, 1226.0, 1188.0, 1146.0, 1154.0, 1161.0, 1199.0, 1184.0, 1336.0, 1233.0, 1173.0, 1161.0, 1158.0, 1165.0, 1165.0, 1165.0, 1180.0, 1161.0, 1177.0, 1226.0, 1173.0, 1211.0, 1184.0, 1180.0, 1203.0, 1146.0, 1196.0, 1226.0, 1165.0, 1177.0, 1154.0, 1184.0, 1180.0, 1158.0, 1199.0, 1215.0, 1241.0, 1215.0, 1211.0, 1199.0, 1161.0, 1139.0, 1199.0, 1218.0, 1165.0, 1184.0, 1252.0, 1192.0, 1192.0, 1226.0, 1241.0, 1146.0, 1226.0, 1230.0, 1203.0, 1139.0, 1135.0, 1184.0, 1196.0, 1097.0, 1139.0, 1142.0, 1180.0, 1169.0, 1154.0, 1142.0, 1173.0, 1158.0, 1199.0, 1218.0, 1196.0, 1146.0, 1165.0, 1177.0, 1146.0, 1207.0, 1196.0, 1165.0, 1184.0, 1260.0, 1252.0, 1177.0, 1230.0, 1203.0, 1218.0, 1196.0, 1218.0, 1207.0, 1207.0, 1256.0, 1211.0, 1169.0, 1215.0, 1233.0, 1233.0, 1196.0, 1177.0, 1218.0, 1199.0, 1192.0, 1142.0, 1139.0, 1192.0, 1207.0, 1169.0, 1196.0, 1233.0, 1218.0, 1192.0, 1218.0, 1158.0, 1222.0, 1222.0, 1180.0, 1158.0, 1142.0, 1161.0, 1222.0, 1298.0, 1199.0, 1245.0, 1211.0, 1180.0, 1161.0, 1127.0, 1161.0, 1108.0, 1154.0, 1165.0, 1188.0, 1184.0, 1188.0, 1177.0, 1283.0, 1237.0, 1215.0, 1150.0, 1180.0, 1184.0, 1207.0, 1241.0, 1230.0, 1177.0, 1226.0, 1180.0, 1131.0, 1127.0, 1207.0, 1116.0, 1165.0, 1199.0, 1222.0, 1188.0, 1142.0, 1150.0, 1215.0, 1165.0, 1177.0, 1203.0, 1237.0, 1290.0, 1199.0, 1211.0, 1184.0, 1199.0, 1196.0, 1211.0, 1150.0, 1192.0, 1192.0, 1192.0, 1222.0, 1237.0, 1161.0, 1237.0, 1177.0, 1199.0, 1131.0, 1230.0, 1245.0, 1177.0, 1104.0, 1158.0, 1135.0, 1218.0, 1218.0, 1264.0, 1252.0, 1180.0, 1203.0, 1268.0, 1188.0, 1279.0, 1207.0, 1139.0, 1173.0, 1192.0, 1120.0, 1158.0, 1196.0, 1218.0, 1230.0, 1264.0, 1211.0, 1203.0, 1226.0, 1226.0, 1222.0, 1207.0, 1321.0, 1192.0, 1173.0, 1161.0, 1203.0, 1165.0, 1165.0, 1199.0, 1203.0, 1177.0, 1211.0, 1177.0, 1237.0, 1215.0, 1222.0, 1256.0, 1249.0, 1192.0, 1199.0, 1275.0, 1264.0, 1241.0, 1264.0, 1226.0, 1222.0, 1161.0, 1131.0, 1207.0, 1184.0, 1154.0, 1161.0, 1207.0, 1207.0, 1279.0, 1290.0, 1298.0, 1226.0, 1192.0, 1199.0, 1188.0, 1264.0, 1199.0, 1188.0, 1184.0, 1177.0, 1173.0, 1165.0, 1211.0, 1188.0, 1165.0, 1139.0, 1180.0, 1218.0, 1150.0, 1192.0, 1188.0, 1245.0, 1139.0, 1249.0, 1218.0, 1146.0, 1165.0, 1233.0, 1161.0, 1180.0, 1154.0, 1177.0, 1184.0, 1139.0, 1173.0, 1188.0, 1203.0, 1169.0, 1135.0, 1188.0, 1196.0, 1237.0, 1196.0, 1260.0, 1211.0, 1245.0, 1180.0, 1211.0, 1139.0, 1184.0, 1245.0, 1218.0, 1237.0, 1135.0, 1188.0, 1222.0, 1218.0, 1180.0, 1264.0, 1207.0, 1230.0, 1203.0, 1173.0, 1158.0, 1203.0, 1317.0, 1150.0, 1112.0, 1074.0, 1169.0, 1180.0, 1211.0, 1268.0, 1199.0, 1230.0, 1218.0, 1260.0, 1222.0, 1230.0, 1173.0, 1177.0, 1139.0, 1203.0, 1173.0, 1188.0, 1249.0, 1256.0, 1222.0, 1271.0, 1188.0, 1180.0, 1146.0, 1203.0, 1199.0, 1237.0, 1215.0, 1215.0, 1215.0, 1173.0, 1233.0, 1245.0, 1173.0, 1207.0, 1177.0, 1188.0, 1226.0, 1279.0, 1173.0, 1203.0, 1184.0, 1177.0, 1196.0, 1230.0, 1184.0, 1146.0, 1199.0, 1271.0, 1237.0, 1256.0, 1207.0, 1268.0, 1215.0, 1161.0, 1188.0, 1184.0, 1188.0, 1241.0, 1249.0, 1317.0, 1211.0, 1218.0, 1222.0, 1226.0, 1241.0, 1237.0, 1245.0, 1154.0, 1218.0, 1264.0, 1290.0, 1230.0, 1154.0, 1184.0, 1256.0, 1233.0, 1241.0, 1188.0, 1215.0, 1173.0, 1165.0, 1211.0, 1180.0, 1146.0, 1199.0, 1271.0, 1317.0, 1237.0, 1226.0, 1252.0, 1359.0, 1142.0, 1207.0, 1173.0, 1188.0, 1226.0, 1215.0, 1196.0, 1154.0, 1169.0, 1222.0, 1256.0, 1256.0, 1249.0, 1169.0, 1161.0, 1180.0, 1249.0, 1237.0, 1222.0, 1237.0, 1237.0, 1218.0, 1180.0, 1215.0, 1218.0, 1165.0, 1173.0, 1268.0, 1207.0, 1290.0, 1268.0, 1154.0, 1196.0, 1230.0, 1256.0, 1211.0, 1241.0, 1218.0, 1290.0, 1306.0, 1173.0, 1184.0, 1249.0, 1207.0, 1260.0, 1215.0, 1222.0, 1207.0, 1196.0, 1184.0, 1215.0, 1150.0, 1230.0, 1241.0, 1188.0, 1215.0, 1215.0, 1245.0, 1180.0, 1188.0, 1199.0, 1192.0, 1230.0, 1233.0, 1184.0, 1146.0, 1233.0, 1211.0, 1203.0, 1230.0, 1199.0, 1271.0, 1260.0, 1184.0, 1184.0, 1268.0, 1230.0, 1233.0, 1226.0, 1241.0, 1207.0, 1226.0, 1215.0, 1150.0, 1249.0, 1199.0, 1211.0, 1165.0, 1116.0, 1169.0, 1233.0, 1188.0, 1196.0, 1184.0, 1188.0, 1142.0, 1184.0, 1196.0, 1169.0, 1188.0, 1180.0, 1154.0, 1211.0, 1222.0, 1169.0, 1196.0, 1196.0, 1237.0, 1245.0, 1222.0, 1207.0, 1203.0, 1218.0, 1237.0, 1184.0, 1199.0, 1142.0, 1150.0, 1226.0, 1226.0, 1233.0, 1222.0, 1245.0, 1290.0, 1241.0, 1252.0, 1264.0, 1196.0, 1196.0, 1165.0, 1215.0, 1328.0, 1290.0, 1530.0, 1260.0, 1294.0, 1218.0, 1211.0, 1207.0, 1211.0, 1249.0, 1294.0, 1344.0, 1309.0, 1290.0, 1279.0, 1222.0, 1222.0, 1275.0, 1480.0, 1336.0, 1252.0, 1302.0, 1260.0, 1294.0, 1241.0, 1154.0, 1146.0, 1196.0, 1188.0, 1199.0, 1158.0, 1207.0, 1180.0, 1161.0, 1150.0, 1161.0, 1199.0, 1249.0, 1165.0, 1245.0, 1226.0, 1203.0, 1230.0, 1226.0, 1256.0, 1222.0, 1218.0, 1142.0, 1207.0, 1207.0, 1199.0, 1222.0, 1260.0, 1233.0, 1230.0, 1211.0, 1215.0, 1230.0, 1218.0, 1302.0, 1252.0, 1215.0, 1161.0, 1184.0, 1184.0, 1215.0, 1150.0, 1199.0, 1169.0, 1177.0, 1173.0, 1211.0, 1237.0, 1196.0, 1184.0, 1211.0, 1222.0, 1207.0, 1230.0, 1180.0, 1207.0, 1290.0, 1188.0, 1177.0, 1256.0, 1237.0, 1309.0, 1218.0, 1378.0, 1245.0, 1188.0, 1252.0, 1199.0, 1192.0, 1271.0, 1317.0, 1237.0, 1188.0, 1256.0, 1211.0, 1226.0, 1173.0, 1199.0, 1165.0, 1192.0, 1203.0, 1218.0, 1252.0, 1287.0, 1150.0, 1226.0, 1211.0, 1222.0, 1290.0, 1218.0, 1207.0, 1211.0, 1207.0, 1184.0, 1230.0, 1158.0, 1222.0, 1218.0, 1184.0, 1184.0, 1184.0, 1199.0, 1139.0, 1154.0, 1264.0, 1215.0, 1233.0, 1158.0, 1245.0, 1309.0, 1306.0, 1287.0, 1298.0, 1537.0, 1199.0, 1252.0, 1215.0, 1256.0, 1177.0, 1233.0, 1177.0, 1142.0, 1142.0, 1173.0, 1241.0, 1256.0, 1287.0, 1332.0, 1313.0, 1215.0, 1222.0, 1298.0, 1237.0, 1233.0, 1218.0, 1256.0, 1249.0, 1268.0, 1252.0, 1256.0, 1279.0, 1207.0, 1260.0, 1260.0, 1207.0, 1332.0, 1245.0, 1196.0, 1283.0, 1233.0, 1184.0, 1177.0, 1169.0, 1199.0, 1203.0, 1188.0, 1249.0, 1268.0, 1184.0, 1215.0, 1154.0, 1237.0, 1146.0, 1142.0, 1188.0, 1184.0, 1188.0, 1237.0, 1283.0, 1218.0, 1165.0, 1150.0, 1233.0, 1188.0, 1211.0, 1226.0, 1203.0, 1215.0, 1237.0, 1233.0, 1199.0, 1241.0, 1249.0, 1211.0, 1203.0, 1199.0, 1226.0, 1196.0, 1184.0, 1245.0, 1275.0, 1256.0, 1233.0, 1252.0, 1279.0, 1287.0, 1606.0, 1192.0, 1165.0, 1203.0, 1211.0, 1211.0, 1215.0, 1177.0, 1218.0, 1211.0, 1233.0, 1222.0, 1218.0, 1230.0, 1192.0, 1123.0, 1169.0, 1188.0, 1196.0, 1245.0, 1256.0, 1249.0, 1222.0, 1222.0, 1207.0, 1230.0, 1264.0, 1226.0, 1233.0, 1256.0, 1279.0, 1245.0, 1199.0, 1215.0, 1256.0, 1245.0, 1249.0, 1192.0, 1203.0, 1207.0, 1230.0, 1218.0, 1203.0, 1215.0, 1207.0, 1207.0, 1283.0, 1207.0, 1226.0, 1226.0, 1279.0, 1203.0, 1294.0, 1287.0, 1245.0, 1192.0, 1237.0, 1230.0, 1230.0, 1252.0, 1180.0, 1245.0, 1222.0, 1252.0, 1279.0, 1279.0, 1298.0, 1237.0, 1215.0, 1264.0, 1249.0, 1249.0, 1252.0, 1211.0, 1203.0, 1233.0, 1154.0, 1215.0, 1252.0, 1237.0, 1294.0, 1309.0, 1279.0, 1252.0, 1249.0, 1294.0, 1283.0, 1222.0, 1199.0, 1237.0, 1230.0, 1222.0, 1264.0, 1241.0, 1241.0, 1211.0, 1252.0, 1252.0, 1233.0, 1271.0, 1260.0, 1249.0, 1207.0, 1237.0, 1230.0, 1298.0, 1230.0, 1230.0, 1378.0, 1249.0, 1237.0, 1215.0, 1287.0, 1226.0, 1283.0, 1256.0, 1313.0, 1241.0, 1275.0, 1264.0, 1184.0, 1245.0, 1230.0, 1271.0, 1215.0, 1203.0, 1252.0, 1241.0, 1256.0, 1321.0, 1283.0, 1271.0, 1271.0, 1321.0, 1275.0, 1226.0, 1290.0, 1332.0, 1264.0, 1215.0, 1230.0, 1241.0, 1249.0, 1252.0, 1165.0, 1222.0, 1309.0, 1252.0, 1237.0, 1252.0, 1283.0, 1264.0, 1298.0, 1302.0, 1275.0, 1268.0, 1256.0, 1177.0, 1279.0, 1249.0, 1256.0, 1252.0, 1306.0, 1222.0, 1245.0, 1309.0, 1188.0, 1279.0, 1298.0, 1268.0, 1233.0, 1249.0, 1332.0, 1207.0, 1233.0, 1256.0, 1233.0, 1203.0, 1264.0, 1215.0, 1215.0, 1271.0, 1268.0, 1340.0, 1340.0, 1309.0, 1218.0, 1211.0, 1215.0, 1252.0, 1275.0, 1268.0, 1233.0, 1249.0, 1249.0, 1252.0, 1226.0, 1412.0, 1268.0, 1279.0, 1237.0, 1207.0, 1233.0, 1196.0, 1222.0, 1188.0, 1226.0, 1275.0, 1196.0, 1222.0, 1256.0, 1192.0, 1169.0, 1192.0, 1203.0, 1177.0, 1207.0, 1203.0, 1192.0, 1165.0, 1131.0, 1123.0, 1108.0, 1131.0, 1161.0, 1135.0, 1169.0, 1146.0, 1127.0]
  // }
  // console.log(deviceSerial, scanType, "background");
  return useQuery(
    "getMeasurementBackground",
    async () => {
      const { data } = await axioslocal.post(
        `${process.env.REACT_APP_CONNECTOR_ROUTE}/get-measurement`,
        {
          deviceSerial: deviceSerial,
          scanType: scanType,
        }
      );
      return data;
    },
    {
      enabled: false,
      retry: false,
      onError: errorLogger,
    }
  );
};

// get measurement data (trace scan(s)) from connected device
export const useGetMeasurementTrace = (deviceSerial, scanType, errorLogger) => {
  console.log(deviceSerial, scanType);
  return useQuery(
    "getMeasurementTrace",
    async () => {
      const { data } = await axioslocal.post(
        `${process.env.REACT_APP_CONNECTOR_ROUTE}/get-trace-measurement`,
        {
          deviceSerial: deviceSerial,
          scanType: scanType,
        }
      );
      return data;
    },
    {
      enabled: false,
      retry: false,
      onError: errorLogger,
    }
  );
};

// get measurement data (bulk scan(s)) from connected device
export const useGetMeasurementBulk = (deviceSerial, scanType, errorLogger) => {
  console.log(deviceSerial, scanType);
  return useQuery(
    "getMeasurementBulk",
    async () => {
      const { data } = await axioslocal.post(
        `${process.env.REACT_APP_CONNECTOR_ROUTE}/get-bulk-measurement`,
        {
          deviceSerial: deviceSerial,
          scanType: scanType,
        }
      );
      return data;
    },
    {
      enabled: false,
      retry: false,
      onError: errorLogger,
    }
  );
};

// Sends a given sample for validation.
export const useValidateSample = (sample, errorLogger) => {
  return useQuery(
    ["validateData", sample],
    async () => {
      console.log("Validating");
      console.log(sample);
      // return {
      //   data: {
      //     errorFlag: 0,
      //   },
      // };
      return await axios.post(`/food/verify-sample`, sample);
    },
    {
      // Don't run query by default
      timeout: 1000,
      enabled: false,
      onError: errorLogger,
    }
  );
};

export const useReferenceSpectrum = (findings) => {
  const results = useQueries(
    findings.map((finding) => {
      return {
        queryKey: ["getReferenceSpectrum", finding.collection, finding.analyte],
        queryFn: async () => {
          const { data } = await axios.get(
            "/food/view-edit-sample/get-reference-spectrum",
            {
              params: {
                collection: finding.collection,
                analyte: finding.analyte,
              },
            }
          );

          if (typeof data === "object") {
            data.scanType = finding.collection;
          }

          return data;
        },
      };
    }),
    {
      enabled: !!findings,
      retry: false,
    }
  );

  let isLoading = results.some((query) => query.isLoading);
  let isError = results.some((query) => query.isError);
  let isSuccess =
    results.length !== 0 && results.every((query) => query.isSuccess);

  if (isError) {
    console.error("An error occurred while fetching data.");
  }

  return { isLoading, isError, isSuccess, results };
};

export const useLogQueryCache = () => {
  const queryClient = useQueryClient();
  return () => {
    console.log("React Query Cache:", queryClient.getQueryCache().findAll());
  };
};
